import Vue from 'vue';
import { mapGetters } from 'vuex';
import { formatDate } from '@/mixins/format-date';
import _ from 'lodash';
import { cognitoMixins } from '@storm/mixins/cognito';
import tooltip from '@/mixins/components/tooltip';
import dropsLoader from '@/mixins/components/drops-loader';
import { DEFAULT_PROFILE_PIC } from '@/constants';
import moment from 'moment-timezone';
import DeleteRougueUser from './delete-rouge-user';

export default Vue.component('storm-dashboard', {
  template: require('./template.html'),
  name: 'storm-dashboard',
  mixins: [formatDate, cognitoMixins],
  components: {
    DeleteRougueUser,
    tooltip,
    dropsLoader,
  },
  computed: {
    ...mapGetters([
      'allUsers',
      'stormSession',
      'stormUser',
      'cache',
    ]),
    filteredUsers() {
      const cleanList = _.map(this.allUsers, user => ({
        _id: user.Username,
        sub: user.Username,
        email: this.getAttr(user.Attributes, 'email'),
        name: this.getAttr(user.Attributes, 'name'),
        stripeId: this.getAttr(user.Attributes, 'custom:stripe_id'),
        identity_id: this.getAttr(user.Attributes, 'custom:identity_id'),
        website: this.getAttr(user.Attributes, 'website'),
        sub_user_owner: this.getAttr(user.Attributes, 'custom:sub_user_owner'),
        status: user.UserStatus,
        created: user.UserCreateDate,
        lastEvent: this.getAttr(user.Attributes, 'custom:last_event') || user.UserCreateDate,
        picture: this.getAttr(user.Attributes, 'picture') || DEFAULT_PROFILE_PIC,
        attrs: user.Attributes,
      }));

      const sortOn = this.sortKey || this.cols[2];
      return _.chain(cleanList)
        .thru((t) => {
          if (this.filterKey) {
            return _.reduce(
              t,
              (acc, row) => {
                // only gather the cols the table needs from data
                const e = [];
                _.forEach([...this.cols, 'identity_id'], (c) => {
                  // clean up the values so that the
                  // searchable items are like how
                  // the template is written
                  const col = this.cleanItem(row[c], c, row);
                  e.push(col);
                });
                if (
                  _.filter(e, v =>
                    _(v)
                      .toLower()
                      .includes(this.filterKey.toLowerCase()),
                  ).length
                ) {
                  acc.push(row);
                }
                return acc;
              },
              [],
            );
          }
          return t;
        })
        .sort((a, b) => {
          // We'll sort accending by default and we'll also ignore case
          // since that may confuse non-technical users
          const ascDesc = this.sortMap[sortOn];
          a = _.isString(a[sortOn]) ? _.toLower(a[sortOn]) : a[sortOn]; // eslint-disable-line
          b = _.isString(b[sortOn]) ? _.toLower(b[sortOn]) : b[sortOn]; // eslint-disable-line
          return (a === b ? 0 : a < b ? -1 : 1) * ascDesc; // eslint-disable-line
        })
        .value();
    },
  },
  data() {
    const cols = ['email', 'name', 'lastEvent', 'status', 'created'];
    const sortMap = _.zipObject(cols, _.fill(Array(cols.length), -1));
    return {
      cols,
      sortMap,
      sortKey: '',
      filterKey: '',
      paginate: ['userlist'],
      loading: false,
      gettingUsers: false,
      chosenUser: null,
      chosenIndex: null,
      modals: {
        confirmRougeDelete: false,
      },
    };
  },
  created() {
    // reset current user first
    this.$store.dispatch('setCurrentUser', false);
    this.getUsers();
  },
  methods: {
    async openAnalytics(user) {
      try {
        this.loading = true;
        this.error = false;
        const userData = await this.stormInvoke({
          data: {
            method: 'getUserEvents',
            chosenUser: user,
          },
        });
        const events = userData.data.events;
        this.$store.dispatch('setUserEvents', events);
        this.$store.dispatch('setEventUser', user);
        this.$router.push({ path: '/storm-analytics' });
      } catch (e) {
        this.$log.error(e.message || e.body || e);
        this.error = e.message || e.body || e;
      } finally {
        this.loading = false;
      }
    },
    async openDrzzle(user) {
      try {
        this.loading = true;
        this.error = false;
        this.chosenUser = user;
        const { data } = await this.stormInvoke({
          data: {
            method: 'getUserData',
            chosenUser: user,
          },
        });
        const payload = data.userData;
        this.setCognitoUserAttrs(user);
        this.$store.dispatch('setCurrentUser', { values: payload, vm: this });
        this.$store.dispatch('setAccount', payload);
        this.$store.dispatch('setUserIdentitiyId', payload._id);
        this.$store.dispatch('setWebsites', { websites: payload.websites });
        this.$store.dispatch('setGlobalValue', {
          item: this.cache,
          key: 'tickets',
          value: false,
        });
        this.$router.push({ path: '/websites' });
      } catch (e) {
        this.$log.error(e.message || e.body || e);
        this.error = e.message || e.body || e;
      } finally {
        this.loading = false;
      }
    },
    setCognitoUserAttrs(user) {
      const attrs = {
        sub: user.sub,
        name: user.name,
        email: user.name,
      };
      if (user.website) {
        attrs.website = user.website;
      }
      if (user.picture) {
        attrs.picture = user.picture;
      }
      if (user.stripeId) {
        attrs.stripeId = user.stripeId;
      }
      if (user.sub_user_owner) {
        attrs.sub_user_owner = user.sub_user_owner;
      }
      this.$store.dispatch('setUserAttributes', user.attrs);
      return this.$store.dispatch('setCurrentUser', { values: attrs, vm: this });
    },
    setSortKey(k) {
      this.sortKey = k;
      // Invert the value for ascDesc
      this.sortMap[k] = this.sortMap[k] * -1;
    },
    getAttr(list, type) {
      const attr = _.find(list, { Name: type });
      return attr ? attr.Value : null;
    },
    seperateCamalCase(str) {
      return str.replace(/([A-Z])/g, g => ` ${g[0].toLowerCase()}`);
    },
    async getUsers(refresh) {
      if ((this.allUsers.length < 1 && !refresh) || refresh) {
        this.gettingUsers = true;
        try {
          const userData = await this.stormInvoke({
            data: {
              method: 'getUsers',
            },
          });
          this.$store.dispatch('setAllUsers', userData.data.Users);
        } catch (e) {
          this.$log.error(e.message || e);
        } finally {
          this.gettingUsers = false;
        }
      }
    },
    onSearch(e) {
      this._debounce(() => {
        this.filterKey = e.target.value;
      }, 250);
    },
    formatTime(stamp) {
      return moment(stamp).format('MM-DD-YYYY hh:mm:ss A');
    },
    cleanItem(item, col) {
      let i = item || '--';
      if (col === 'created' && i !== '--') {
        i = new Date(i);
        i = this.formatDate(i.toISOString());
      }
      if (col === 'lastEvent') {
        i = this.formatTime(item);
      }
      return i;
    },
    toggleConfirm(value, user) {
      if (user) {
        this.chosenUser = user;
      }
      this.modals.confirmRougeDelete = value;
    },
    highlight(ctx, query, col) {
      const c = this.cleanItem(ctx, col);
      return c.replace(
        new RegExp(query, 'ig'),
        match => `<span class='editorUi-searchHighlight'>${match}</span>`,
      );
    },
  },
});



// WEBPACK FOOTER //
// ./src/storm/pages/drzzle-accounts/index.js